import { X } from "lucide-react";
import { useEngineQuery } from "@/engine/hooks";
import { Button } from "@/ui";
import { removeTypedClause, type FilterPart, type FilterPartKey } from "./filter";
import s from "./filter.module.css";

/** The chips under a title that explain an active query in the engine's own words, each removable. */
export function ChipRow({ chips, onRemove, onClear }: { chips: string[]; onRemove?: (index: number) => void; onClear?: () => void }) {
  if (chips.length === 0) return null;
  return (
    <div className={s.chipRow} data-testid="query-chips">
      {chips.map((chip, i) => (
        <span key={`${i}-${chip}`} className={s.chip}>
          {chip}
          {onRemove && (
            <button type="button" onClick={() => onRemove(i)} aria-label={`Remove ${chip}`} title="Remove">
              <X size={11} />
            </button>
          )}
        </span>
      ))}
      {chips.length >= 2 && onClear && <Button size="sm" variant="ghost" onClick={onClear}>Clear</Button>}
    </div>
  );
}

/**
 * Chips for the panel's clauses and the typed query, from the engine's own parser. The panel's clauses map one to
 * one onto their chips, so an x clears that row; a typed chip drops its clause from the box.
 */
export function useQueryChips({ parts, typed, onClearPart, onReset, onTyped }: { parts: FilterPart[]; typed: string; onClearPart: (key: FilterPartKey) => void; onReset: () => void; onTyped: (q: string) => void }) {
  const grammar = parts.map((p) => p.clause).join(" ");
  const parsedFilter = useEngineQuery("parseSearch", { query: grammar }, { enabled: grammar.length > 0, staleTime: 60_000, placeholderData: (p) => p });
  const parsedTyped = useEngineQuery("parseSearch", { query: typed.trim() }, { enabled: typed.trim().length > 0, staleTime: 60_000, placeholderData: (p) => p });
  const filterChips = grammar ? parsedFilter.data?.chips ?? [] : [];
  const typedChips = typed.trim() ? parsedTyped.data?.chips ?? [] : [];
  const chips = [...filterChips, ...typedChips];
  const remove = (i: number) => {
    if (i < filterChips.length) {
      if (filterChips.length === parts.length) onClearPart(parts[i].key);
      else onReset();
      return;
    }
    const ast = parsedTyped.data?.ast;
    onTyped(ast ? removeTypedClause(ast, typedChips.length, i - filterChips.length) : "");
  };
  const clear = () => {
    onReset();
    onTyped("");
  };
  return { chips, remove, clear };
}
